import { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    ClickElement,
    trackChatbotCloseButtonClick,
    trackChatbotOpenButtonClick,
    trackChatbotProductRecommendationClick,
    trackChatbotProductRecommendationView,
    trackChatbotSendMessage,
} from '@jsmdg/tracking';
import { Breakpoint } from '@jsmdg/yoshi/dist/enums/breakpoint';
import { useBreakpoint } from '@jsmdg/yoshi/dist/hooks/useBreakpoint';
import { type FooterContext } from '../../types';
import { FeatureFlagVariant } from '../../types/enums/featureFlagVariant';
import { getTenantCondition } from '../../utils/getTenantCondition';
import { ChatBotHelpTooltip } from './ChatBotHelpTooltip';
import styles from './ChatBot.module.scss';

const CHATBOT_SESSION_STORAGE_KEY = 'evelin-footer-fragment.chat-bot-open';
const CHATBOT_HELP_TOOLTIP_SESSION_KEY = 'evelin-footer-fragment.chat-bot-help-tooltip-dismissed';
const CHATBOT_HELP_TOOLTIP_ID = 'chatbot-help-tooltip';
const CHATBOT_HELP_TOOLTIP_SHOW_DELAY_MS = 10_000;

const PRODUCT_DETAIL_FRAGMENT_SELECTOR = '[data-id="product-detail-fragment"]';
const BUYBOX_CALL_TO_ACTION_SELECTOR = "[class*='Buybox_callToActionContainer']";
const BUYBOX_HIDE_CALL_TO_ACTION_SELECTOR = "[class*='Buybox_hideCallToActionContainer']";

const getBuyboxCallToActionHeightPx = (): number => {
    if (typeof document === 'undefined') {
        return 0;
    }

    if (document.querySelector(BUYBOX_HIDE_CALL_TO_ACTION_SELECTOR)) {
        return 0;
    }

    const el = document.querySelector(BUYBOX_CALL_TO_ACTION_SELECTOR);
    if (!el || !(el instanceof HTMLElement)) {
        return 0;
    }

    return Math.round(el.getBoundingClientRect().height);
};

const readChatBotOpenFromSession = (): boolean => {
    try {
        return window?.sessionStorage?.getItem(CHATBOT_SESSION_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const writeChatBotOpenToSession = (open: boolean): void => {
    try {
        window?.sessionStorage?.setItem(CHATBOT_SESSION_STORAGE_KEY, open ? 'true' : 'false');
        // eslint-disable-next-line no-empty
    } catch {}
};

const readHelpTooltipDismissedFromSession = (): boolean => {
    try {
        return window?.localStorage?.getItem(CHATBOT_HELP_TOOLTIP_SESSION_KEY) === 'true';
    } catch {
        return false;
    }
};

const writeHelpTooltipDismissedToSession = (dismissed: boolean): void => {
    try {
        window?.localStorage?.setItem(
            CHATBOT_HELP_TOOLTIP_SESSION_KEY,
            dismissed ? 'true' : 'false',
        );
        // eslint-disable-next-line no-empty
    } catch {}
};

const messages = defineMessages({
    closeChat: { defaultMessage: 'Chat schließen' },
    closeTooltip: { defaultMessage: 'Schließen' },
    openChat: { defaultMessage: 'Chat öffnen' },
    helpTooltipText: { defaultMessage: 'Möchtest du Hilfe bei der Auswahl?' },
});

const ChatIcon = ({ isMydays }: { readonly isMydays: boolean }): JSX.Element => (
    <svg width="35" viewBox="0 0 45 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M28.2958 1.18134C28.3582 0.849399 28.5355 0.549593 28.7971 0.333847C29.0586 0.1181 29.3879 0 29.7279 0C30.0679 0 30.3972 0.1181 30.6588 0.333847C30.9203 0.549593 31.0976 0.849399 31.1601 1.18134L32.6913 9.22389C32.8 9.79568 33.0798 10.3216 33.4941 10.7331C33.9084 11.1446 34.4379 11.4224 35.0136 11.5304L43.1112 13.0513C43.4454 13.1133 43.7472 13.2894 43.9644 13.5492C44.1817 13.809 44.3006 14.136 44.3006 14.4737C44.3006 14.8114 44.1817 15.1384 43.9644 15.3982C43.7472 15.658 43.4454 15.8341 43.1112 15.8961L35.0136 17.4169C34.4379 17.5249 33.9084 17.8028 33.4941 18.2143C33.0798 18.6257 32.8 19.1517 32.6913 19.7235L31.1601 27.766C31.0976 28.098 30.9203 28.3978 30.6588 28.6135C30.3972 28.8293 30.0679 28.9474 29.7279 28.9474C29.3879 28.9474 29.0586 28.8293 28.7971 28.6135C28.5355 28.3978 28.3582 28.098 28.2958 27.766L26.7646 19.7235C26.6558 19.1517 26.376 18.6257 25.9618 18.2143C25.5475 17.8028 25.0179 17.5249 24.4422 17.4169L16.3447 15.8961C16.0105 15.8341 15.7086 15.658 15.4914 15.3982C15.2742 15.1384 15.1553 14.8114 15.1553 14.4737C15.1553 14.136 15.2742 13.809 15.4914 13.5492C15.7086 13.2894 16.0105 13.1133 16.3447 13.0513L24.4422 11.5304C25.0179 11.4224 25.5475 11.1446 25.9618 10.7331C26.376 10.3216 26.6558 9.79568 26.7646 9.22389L28.2958 1.18134Z"
            fill="url(#paint0_linear_4613_34833)"
        />
        <path
            d="M9.98678 18.267C10.0342 18.0147 10.169 17.7868 10.3678 17.6229C10.5666 17.4589 10.8168 17.3691 11.0752 17.3691C11.3336 17.3691 11.5839 17.4589 11.7826 17.6229C11.9814 17.7868 12.1162 18.0147 12.1636 18.267L13.3274 24.3793C13.41 24.8139 13.6227 25.2136 13.9375 25.5263C14.2524 25.839 14.6548 26.0502 15.0923 26.1323L21.2465 27.2881C21.5005 27.3352 21.7299 27.4691 21.895 27.6665C22.0601 27.8639 22.1504 28.1125 22.1504 28.3691C22.1504 28.6258 22.0601 28.8743 21.895 29.0718C21.7299 29.2692 21.5005 29.4031 21.2465 29.4502L15.0923 30.606C14.6548 30.6881 14.2524 30.8993 13.9375 31.212C13.6227 31.5247 13.41 31.9244 13.3274 32.359L12.1636 38.4713C12.1162 38.7236 11.9814 38.9515 11.7826 39.1154C11.5839 39.2794 11.3336 39.3691 11.0752 39.3691C10.8168 39.3691 10.5666 39.2794 10.3678 39.1154C10.169 38.9515 10.0342 38.7236 9.98678 38.4713L8.82305 32.359C8.7404 31.9244 8.52778 31.5247 8.21292 31.212C7.89807 30.8993 7.49562 30.6881 7.05809 30.606L0.903958 29.4502C0.649957 29.4031 0.420547 29.2692 0.255458 29.0718C0.0903698 28.8743 0 28.6258 0 28.3691C0 28.1125 0.0903698 27.8639 0.255458 27.6665C0.420547 27.4691 0.649957 27.3352 0.903958 27.2881L7.05809 26.1323C7.49562 26.0502 7.89807 25.839 8.21292 25.5263C8.52778 25.2136 8.7404 24.8139 8.82305 24.3793L9.98678 18.267Z"
            fill="url(#paint1_linear_4613_34833)"
        />
        <defs>
            <linearGradient
                id="paint0_linear_4613_34833"
                x1="16.7775"
                y1="28.9474"
                x2="45.5468"
                y2="3.20577"
                gradientUnits="userSpaceOnUse"
            >
                <stop
                    offset="0.211538"
                    stopColor={isMydays ? styles.chatBotColorPurple : styles.chatBotColorTeal}
                />
                <stop
                    offset="0.716346"
                    stopColor={isMydays ? styles.chatBotColorRed : styles.chatBotColorBlue}
                />
            </linearGradient>
            <linearGradient
                id="paint1_linear_4613_34833"
                x1="1.23287"
                y1="39.3691"
                x2="23.0975"
                y2="19.8055"
                gradientUnits="userSpaceOnUse"
            >
                <stop
                    offset="0.211538"
                    stopColor={isMydays ? styles.chatBotColorPurple : styles.chatBotColorTeal}
                />
                <stop
                    offset="0.716346"
                    stopColor={isMydays ? styles.chatBotColorRed : styles.chatBotColorBlue}
                />
            </linearGradient>
        </defs>
    </svg>
);

const ChatIconClose = ({ isMydays }: { readonly isMydays: boolean }): JSX.Element => (
    <svg width="23" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M25.4119 0.745661C24.3119 -0.354339 22.7619 -0.136839 21.8769 0.745661L13.0369 9.58566L4.19687 0.745661C3.31437 -0.136839 1.54688 -0.136839 0.661875 0.745661C-0.220625 1.63066 -0.220625 3.39566 0.661875 4.28066L9.50188 13.1207L0.661875 21.9607C-0.220625 22.8432 -0.220625 24.6107 0.661875 25.4957C1.54688 26.3782 3.31187 26.3782 4.19687 25.4957L13.0369 16.6557L21.8769 25.4957C22.7594 26.3782 24.5269 26.3782 25.4119 25.4957C26.2944 24.6107 26.2944 22.8457 25.4119 21.9607L16.5719 13.1207L25.4119 4.28066C26.2944 3.39816 26.5119 1.84566 25.4119 0.745661Z"
            fill="url(#paint0_linear_4805_2518)"
        />
        <defs>
            <linearGradient
                id="paint0_linear_4805_2518"
                x1="0.205637"
                y1="26.1575"
                x2="26.3599"
                y2="0.414498"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor={isMydays ? styles.chatBotColorPurple : styles.chatBotColorTeal} />
                <stop
                    offset="1"
                    stopColor={isMydays ? styles.chatBotColorRed : styles.chatBotColorBlue}
                />
            </linearGradient>
        </defs>
    </svg>
);

const ChatBot = (): JSX.Element | null => {
    const { chatBotLinks, featureFlagsVariation, tenant } = useFragmentContext<FooterContext>();
    const { isMydays } = getTenantCondition(tenant);

    const chatBotLink = chatBotLinks ? chatBotLinks[tenant] : undefined;

    const isDesktop = useBreakpoint(Breakpoint.SM);
    const isChatBotEnabled = featureFlagsVariation?.chatbot_enabled === FeatureFlagVariant.ON;

    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [buyboxCtaHeightPx, setBuyboxCtaHeightPx] = useState(0);
    const [helpTooltipDismissed, setHelpTooltipDismissed] = useState(true);
    const [helpTooltipDelayElapsed, setHelpTooltipDelayElapsed] = useState(false);

    useEffect(() => {
        setHelpTooltipDismissed(readHelpTooltipDismissedFromSession());
    }, []);

    useEffect(() => {
        if (helpTooltipDismissed) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setHelpTooltipDelayElapsed(true);
        }, CHATBOT_HELP_TOOLTIP_SHOW_DELAY_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [helpTooltipDismissed]);

    useEffect(() => {
        if (isDesktop) {
            setIsOpen(readChatBotOpenFromSession());
        }
    }, [isDesktop]);

    useEffect(() => {
        let rafId = 0;
        let observedEl: Element | null = null;
        let resizeObserver: ResizeObserver | null = null;
        let fragmentMutationObserver: MutationObserver | null = null;
        let bootstrapMutationObserver: MutationObserver | null = null;
        let observedFragmentRoot: Element | null = null;

        const disconnectFragmentMutationObserver = (): void => {
            fragmentMutationObserver?.disconnect();
            fragmentMutationObserver = null;
            observedFragmentRoot = null;
        };

        const disconnectBootstrapMutationObserver = (): void => {
            bootstrapMutationObserver?.disconnect();
            bootstrapMutationObserver = null;
        };

        const scheduleSync = (): void => {
            cancelAnimationFrame(rafId);
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            rafId = requestAnimationFrame(sync);
        };

        const bindMutationObservers = (): void => {
            const fragmentRoot = document.querySelector(PRODUCT_DETAIL_FRAGMENT_SELECTOR);

            if (fragmentRoot) {
                if (fragmentRoot !== observedFragmentRoot) {
                    disconnectFragmentMutationObserver();
                    disconnectBootstrapMutationObserver();
                    observedFragmentRoot = fragmentRoot;
                    fragmentMutationObserver = new MutationObserver(() => {
                        scheduleSync();
                    });
                    fragmentMutationObserver.observe(fragmentRoot, {
                        childList: true,
                        subtree: true,
                        attributes: true,
                    });
                }
            } else {
                disconnectFragmentMutationObserver();
                if (!bootstrapMutationObserver) {
                    bootstrapMutationObserver = new MutationObserver(() => {
                        bindMutationObservers();
                        scheduleSync();
                    });
                    bootstrapMutationObserver.observe(document.body, {
                        childList: true,
                        subtree: true,
                        attributes: true,
                    });
                }
            }
        };

        const sync = (): void => {
            if (observedFragmentRoot !== null && !observedFragmentRoot.isConnected) {
                disconnectFragmentMutationObserver();
                bindMutationObservers();
            }

            const heightPx = getBuyboxCallToActionHeightPx();
            setBuyboxCtaHeightPx(prev => (prev === heightPx ? prev : heightPx));

            const hidePresent = document.querySelector(BUYBOX_HIDE_CALL_TO_ACTION_SELECTOR);
            const cta = document.querySelector(BUYBOX_CALL_TO_ACTION_SELECTOR);
            const target = !hidePresent && cta instanceof HTMLElement ? cta : null;

            if (target !== observedEl) {
                resizeObserver?.disconnect();
                resizeObserver = null;
                observedEl = target;

                if (target && typeof ResizeObserver !== 'undefined') {
                    resizeObserver = new ResizeObserver(() => {
                        scheduleSync();
                    });
                    resizeObserver.observe(target);
                }
            }
        };

        bindMutationObservers();
        scheduleSync();

        return () => {
            cancelAnimationFrame(rafId);
            disconnectFragmentMutationObserver();
            disconnectBootstrapMutationObserver();
            resizeObserver?.disconnect();
        };
    }, []);

    const dismissHelpTooltip = useCallback(() => {
        setHelpTooltipDismissed(true);
        writeHelpTooltipDismissedToSession(true);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        writeChatBotOpenToSession(false);
        trackChatbotCloseButtonClick('x');
    }, []);

    const open = useCallback(
        (openFromSearch: boolean) => {
            setIsOpen(true);
            writeChatBotOpenToSession(true);
            trackChatbotOpenButtonClick(
                openFromSearch ? ClickElement.OpenChatbotAISearch : ClickElement.OpenChatbot,
            );

            dismissHelpTooltip();
        },
        [dismissHelpTooltip],
    );

    const buttonOnClickHandler = useCallback((): void => {
        if (isOpen) {
            close();
        } else {
            open(false);
        }
    }, [close, isOpen, open]);

    useEffect(() => {
        if (!isChatBotEnabled || !chatBotLink) {
            return;
        }

        let chatOrigin: string | undefined;
        try {
            chatOrigin = new URL(chatBotLink).origin;
        } catch {
            chatOrigin = undefined;
        }

        const onMessage = (event: MessageEvent): void => {
            if (event.data?.eventName === 'open_chatbot') {
                if (event.origin !== window.location.origin) {
                    return;
                }

                open(true);
                return;
            }

            if (chatOrigin !== undefined && event.origin !== chatOrigin) {
                return;
            }

            switch (event.data?.eventName) {
                case 'product_suggestion_click':
                    trackChatbotProductRecommendationClick(
                        event.data?.product,
                        event.data?.index,
                        event.data?.sliderIndex,
                    );
                    break;
                case 'product_suggestion_view':
                    trackChatbotProductRecommendationView(
                        event.data?.product,
                        event.data?.index,
                        event.data?.sliderIndex,
                    );
                    break;
                case 'send_message':
                    trackChatbotSendMessage(event.data?.index, ClickElement.TypeAndEnter);
                    break;
                case 'suggestion_click':
                    trackChatbotSendMessage(event.data?.index, ClickElement.ClickSuggestion);
                    break;
            }
        };

        window.addEventListener('message', onMessage);

        // eslint-disable-next-line consistent-return
        return () => window.removeEventListener('message', onMessage);
    }, [chatBotLink, isChatBotEnabled, open]);

    const showHelpTooltip = !helpTooltipDismissed && !isOpen && helpTooltipDelayElapsed;

    if (!isChatBotEnabled || !chatBotLink) {
        return null;
    }

    return (
        <>
            <div
                className={styles.launcherShell}
                style={{ bottom: `calc(1.5rem + ${buyboxCtaHeightPx}px)` }}
            >
                {showHelpTooltip && (
                    <ChatBotHelpTooltip
                        isMydays={isMydays}
                        tooltipId={CHATBOT_HELP_TOOLTIP_ID}
                        closeLabel={intl.formatMessage(messages.closeTooltip)}
                        emoji="👋"
                        onClose={dismissHelpTooltip}
                        bottomShiftPx={buyboxCtaHeightPx}
                    >
                        {intl.formatMessage(messages.helpTooltipText)}
                    </ChatBotHelpTooltip>
                )}
                <button
                    type="button"
                    className={classNames(styles.launcher, 'd-flex justify-content-center', {
                        [styles.launcherMydays]: isMydays,
                    })}
                    onClick={buttonOnClickHandler}
                    aria-label={intl.formatMessage(isOpen ? messages.closeChat : messages.openChat)}
                    aria-describedby={showHelpTooltip ? CHATBOT_HELP_TOOLTIP_ID : undefined}
                >
                    {isOpen ? (
                        <ChatIconClose isMydays={isMydays} />
                    ) : (
                        <ChatIcon isMydays={isMydays} />
                    )}
                </button>
            </div>
            {isOpen && (
                <div
                    className={classNames(styles.popup, 'd-flex')}
                    role="dialog"
                    aria-modal="true"
                    aria-label={intl.formatMessage(messages.openChat)}
                >
                    <div
                        className={classNames(
                            styles.popupHeader,
                            'd-flex justify-content-end d-sm-none',
                        )}
                    >
                        <button
                            type="button"
                            className={classNames(
                                styles.closeButton,
                                'd-inline-flex justify-content-center',
                            )}
                            onClick={close}
                        >
                            <span className={styles.closeButtonLabel}>
                                {intl.formatMessage(messages.closeChat)}
                            </span>
                            <span aria-hidden="true" className={styles.closeButtonIcon}>
                                ×
                            </span>
                        </button>
                    </div>
                    {/* eslint-disable-next-line react/iframe-missing-sandbox */}
                    <iframe
                        title={intl.formatMessage(messages.openChat)}
                        src={chatBotLink}
                        className={styles.iframe}
                    />
                </div>
            )}
        </>
    );
};

export { ChatBot };
